import Service from 'additiv-services';
import { getQueryStringByObject } from 'additiv-services/utils/queryUtils';
import { getCustomServiceLayerHeader } from 'additiv-services/setup';
import {
    CUSTOM_SERVICE_LAYER_SERVICES,
    getCustomServiceLayerBaseUrl,
} from '../constants';

class CustomCaseManagementServices extends Service {
    static #baseServiceUrl = getCustomServiceLayerBaseUrl(
        CUSTOM_SERVICE_LAYER_SERVICES.CASE_ORDERS,
    );

    // GET requests
    static getRetentionReportOfSentDocs({ from, to, language }) {
        const queryString = getQueryStringByObject({
            startDate: from,
            endDate: to,
            language,
        });

        return Service.getRequest(
            `${this.#baseServiceUrl}/documents/empty-form/report?${queryString}`,
            {
                isSecure: true,
                axiosSetup: {
                    headers: { 'accept-language': language },
                    responseType: 'blob',
                },
            },
        );
    }

    static getRetentionReportOfSubmittedOrders({ from, to, language }) {
        const queryString = getQueryStringByObject({
            startDate: from,
            endDate: to,
            language,
        });

        return Service.getRequest(
            `${this.#baseServiceUrl}/report?${queryString}`,
            {
                isSecure: true,
                axiosSetup: {
                    headers: { 'accept-language': language },
                    responseType: 'blob',
                },
            },
        );
    }

    static getCasesByPortfolioId({ portfolioId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/portfolio/${portfolioId}`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getSubmittedCasesByPortfolioId({ portfolioId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/portfolio/${portfolioId}/submitted-orders`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getCaseById({ caseId }) {
        return Service.getRequest(`${this.#baseServiceUrl}/${caseId}`, {
            isSecure: true,
            axiosSetup: { headers: getCustomServiceLayerHeader() },
        });
    }

    static getSubmittedCaseById({ externalId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/external/${externalId}/details`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getActive3bPortfoliosByContactGroup({ contactGroupId, language }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/contact-groups/${contactGroupId}/portfolios?language=${language}`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getBeneficiaries({ portfolioId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/portfolio/${portfolioId}/beneficiaries`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getListOfEmptyFormDocuments({ language }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/documents/empty-form?language=${language}`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getDocumentsByCaseId({ caseId, language }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${caseId}/documents`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getCasesByContainerId({ containerId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/investment-container/${containerId}`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    // POST requests
    static createNewCaseOrder({ language, ...rest }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}?language=${language}`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static downloadEmptyForm({ ...rest }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/documents/empty-form/download`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static generateCaseDocument({ caseId, language }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${caseId}/documents/generate?language=${language}`,
            {},
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static submitCaseOrder({ caseId }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${caseId}/submit`,
            {},
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    // PATCH requests
    static verifySignature({ caseId }) {
        return Service.patchRequest(
            `${this.#baseServiceUrl}/${caseId}/signature-verified`,
            {},
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    // DELETE requests
    static deleteCase({ caseId }) {
        return Service.deleteRequest(`${this.#baseServiceUrl}/${caseId}`, {
            isSecure: true,
            axiosSetup: { headers: getCustomServiceLayerHeader() },
        });
    }
}

export default CustomCaseManagementServices;
